import HealthIcon from "./Icons/HealthIcon.js";
import SpeedIcon from "./Icons/SpeedIcon";
import DamageIcon from "./Icons/DamageIcon";

import { formatNumber, getItemByName, getRandomNumber } from "./Functions";
import { useState, useEffect, useRef } from "react";

import { motion } from "framer-motion";
import { DungeonHpBar } from "./DungeonHpBar.js";
import DamageText from "./DamageTextAnim.js";
import SlashAnimation from "./SlashAnimation.js";
import adventuresList from "./AdventuresList.js";
import ConfirmMessage from "./ConfirmMessage.js";
import audioManager from "./AudioManager.js";
import { useSocket } from "./SocketContext.js";

const Dungeon = ({
  t,
  dungeon,
  aura,
  auraStats,
  setNowOpen,
  farm,
  localProfile,
  addDungeon,
  setSortedItems,
}) => {
  const [showLoot, setShowLoot] = useState(false);
  const [lootList, setLootList] = useState([]);
  const [isConfirmQuitOpen, setIsConfirmQuitOpen] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const { emit, on, off, once, isConnected } = useSocket();
  const handleQuit = () => {
    setIsConfirmQuitOpen(true);
  };

  useEffect(() => {
    return () => {
      clearInterval(auraIntervalRef.current);
      clearInterval(enemyIntervalRef.current);
      hitCount.current = 0;
    };
  }, []);

  const hitCount = useRef(0);

  const roomsQuantity = Array(dungeon.rooms).fill(null);

  const ducksLoot = useRef(0);

  const fetchLoot = async (completed) => {
    setIsFinished(true);
    audioManager.stopAll();
    audioManager.play("s_angels");
    const lootArray = [];
    const itemsArray = []; // Используем const, так как массив не переприсваивается
    lootArray.push({
      title: `${ducksLoot.current} DUCK$`,
      miniimg: "ducks-image.webp",
    });
    const nextDungeonId = dungeon.id + 1;

    if (completed) {
      if (
        nextDungeonId > localProfile.openDungeons &&
        nextDungeonId <= adventuresList.length - 1
      ) {
        addDungeon(nextDungeonId);
        lootArray.push({
          title: adventuresList[nextDungeonId].title,
          miniimg: "open-lock.webp",
        });
      }
      // Используем for...of для обработки асинхронных операций

      for (const item of dungeon.bossLoot) {
        const dice = getRandomNumber(0, 100);
        console.log("DICE: ", dice);

        if (dice <= item.chance) {
          const lootItem = await getItemByName(item.title);
          if (lootItem) {
            lootArray.push(lootItem);
            itemsArray.push(lootItem);
          }
        }
      }

      console.log("Array: ", lootArray);
      setLootList((prev) => [...prev, ...lootArray]); // Добавляем новые элементы в существующий массив
      off("craftItemSuccess");
      emit("itemsUpdate", {
        playerId: localProfile.id,
        itemsToUse: itemsArray,
        action: "add",
      });

      once("itemsUpdateSuccess", async ({ ItemsList }) => {
        setShowLoot(true);
        setSortedItems(ItemsList);
      });
    } else {
      setLootList((prev) => [...prev, ...lootArray]);
      setShowLoot(true);
    }
  };

  const [enemyClass, setEnemyClass] = useState("");
  const changeEnemyClass = () => {
    setEnemyClass("enemy-in");
    setTimeout(() => {
      setEnemyClass("");
    }, 500);
  };

  const [auraSlashAnimations, setAuraSlashAnimations] = useState([]); // Для анимаций aura
  const [enemySlashAnimations, setEnemySlashAnimations] = useState([]); // Для анимаций enemy
  const addAuraSlashAnimation = (crit) => {
    const animationId = Date.now(); // Уникальный идентификатор анимации

    setAuraSlashAnimations((prev) => [
      ...prev,
      { id: animationId, crit: crit },
    ]);

    // Удаляем анимацию через 1 секунду (время завершения анимации)
    setTimeout(() => {
      setAuraSlashAnimations((prev) =>
        prev.filter((anim) => anim.id !== animationId)
      );
    }, 500); // Время должно соответствовать длительности анимации
  };

  const addEnemySlashAnimation = (crit) => {
    const animationId = Date.now(); // Уникальный идентификатор анимации

    setEnemySlashAnimations((prev) => [
      ...prev,
      { id: animationId, crit: crit },
    ]);

    setTimeout(() => {
      setEnemySlashAnimations((prev) =>
        prev.filter((anim) => anim.id !== animationId)
      );
    }, 500); // Время должно соответствовать длительности анимации
  };

  const [roomNumber, setRoomNumber] = useState(1);
  const [enemy, setEnemy] = useState(dungeon.enemies[0]);

  useEffect(() => {
    const updateEnemyInfo = () => {
      setEnemyHpState(enemy.hp);
      enemyHpRef.current = enemy.hp;
    };
    if (roomNumber && enemy) {
      updateEnemyInfo();
    }
  }, [roomNumber, enemy]);

  const auraHpRef = useRef(auraStats.hp);
  const [auraHpState, setAuraHpState] = useState(auraHpRef.current);

  const enemyHpRef = useRef(enemy.hp);
  const [enemyHpState, setEnemyHpState] = useState(enemyHpRef.current);

  const [isFighting, setIsFighting] = useState(false);

  const isCrit = () => {
    if (getRandomNumber(0, 100) > 10) {
      return false;
    } else {
      console.log("crit");
      return true;
    }
  };

  const auraIntervalRef = useRef(null);
  const enemyIntervalRef = useRef(null);

  const startFight = () => {
    setIsFighting(true);

    auraIntervalRef.current = setInterval(() => {
      const critMultiplier = isCrit() ? 2 : 1;
      const damage = Math.ceil(
        auraStats.damage * critMultiplier * (Math.random() * (1.1 - 0.9) + 0.9)
      );

      setEnemyHpState((prevHp) => {
        let newHp = prevHp - damage;
        if (newHp < 0) {
          newHp = 0;
        }

        let soundName;
        if (critMultiplier > 1) {
          soundName = "s_critHit01";
        } else {
          soundName = Math.random() > 0.5 ? "s_hit01" : "s_hit02";
          const randomRate = Math.random() * (1.3 - 0.8) + 0.8;
          audioManager.setRate(soundName, randomRate);
        }

        // Воспроизводим выбранный звук
        audioManager.play(soundName);

        enemyHpRef.current = newHp; // Update ref immediately after state change
        hitCount.current += 1;
        if (newHp <= 0) {
          setIsFighting(false);
          const successFarm = farm(enemy.drop);
          if (successFarm) {
            ducksLoot.current = ducksLoot.current + enemy.drop;

            if (roomNumber <= dungeon.rooms - 1) {
              changeEnemyClass();
              setTimeout(() => {
                if (roomNumber < dungeon.rooms - 1) {
                  const randomEnemy = getRandomNumber(
                    0,
                    dungeon.enemies.length - 1
                  );
                  console.log("Random enemy: ", randomEnemy);
                  setEnemy(dungeon.enemies[randomEnemy]);
                  setRoomNumber((current) => current + 1);
                }

                if (roomNumber === dungeon.rooms - 1) {
                  setRoomNumber((current) => current + 1);
                  setEnemy(dungeon.boss);
                }
              }, 250);
            } else {
              fetchLoot(true);
            }
          }

          clearInterval(auraIntervalRef.current);
          clearInterval(enemyIntervalRef.current);
        }
        return newHp;
      });

      // Dispatch the event AFTER the state update
      const damageEvent = new CustomEvent("aura-damage", {
        detail: {
          damage: critMultiplier > 1 ? "КРИТ " + damage : damage,
        },
      });
      document.dispatchEvent(damageEvent);

      if (critMultiplier > 1) {
        addAuraSlashAnimation(true);
      } else {
        addAuraSlashAnimation(false);
      }
    }, 1000 / auraStats.speed);

    enemyIntervalRef.current = setInterval(() => {
      const critMultiplier = isCrit() ? 2 : 1;
      const damage = Math.ceil(
        enemy.damage * critMultiplier * (Math.random() * (1.1 - 0.9) + 0.9)
      );

      setAuraHpState((prevHp) => {
        let newHp = prevHp - damage;
        if (newHp < 0) {
          newHp = 0;
        }
        auraHpRef.current = newHp; // Update ref immediately after state change

        if (newHp <= 0) {
          fetchLoot(false);
          setIsFighting(false);
          clearInterval(auraIntervalRef.current);
          clearInterval(enemyIntervalRef.current);
        }
        return newHp;
      });

      // Dispatch the event AFTER the state update
      const damageEvent = new CustomEvent("enemy-damage", {
        detail: {
          damage: critMultiplier > 1 ? "КРИТ " + damage : damage,
        },
      });
      document.dispatchEvent(damageEvent);

      if (critMultiplier > 1) {
        addEnemySlashAnimation(true);
      } else {
        addEnemySlashAnimation(false);
      }
    }, 1000 / enemy?.speed);

    // Cleanup intervals on component unmount
    return () => {
      clearInterval(auraIntervalRef.current);
      clearInterval(enemyIntervalRef.current);
    };
  };

  return (
    <div>
      {/* {showLoot && (
        <div className="loot-container">
         

          <div className="loot-items-list">
            {lootList?.map((item, index) => (
              <div className="loot-item-container" key={index}>
                <img alt="loot img" width={60} src={item.miniimg}></img>
                {t(item.title)}
              </div>
            ))}
          </div>
          <motion.button
            whileTap={{
              scale: 0.95,
            }}
            transition={{ duration: 0.1 }}
           
            className="button-ui"
            style={{
              width: "50%",
              marginTop: 20,
              backgroundColor: "rgb(202, 163, 54)",
            }}
          >
            {t("Quit")}
          </motion.button>
        </div>
      )} */}
      {showLoot && (
        <div className="loot-container">
          <div className="loot-text">{t("Loot")}</div>
          <div className="loot-items-info">
            {lootList?.map((item, index) => (
              <div className="loot-item-container" key={index}>
                <img
                  alt="ducks-image"
                  width="100%"
                  style={{ borderRadius: 4 }}
                  src={item.miniimg}
                />
                <div className="loot-item-text">{t(item.title)}</div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <motion.img
              initial={{
                opacity: 0,
                scale: 0.9,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                duration: 0.3,
              }}
              width={"60%"}
              src="chest.webp"
            ></motion.img>
            <img
              alt="star-bg"
              className="win-glowing-bg-star"
              src="/glowing-star2.webp"
            ></img>
          </div>

          <div style={{ display: "flex", width: "60%", gap: 8 }}>
            <button
              onClick={() => {
                audioManager.stopAll();
                audioManager.play("s_adventures");
                setNowOpen("adventures");
              }}
              className="forge-button"
            >
              {t("Quit")}
            </button>
          </div>
        </div>
      )}

      {isConfirmQuitOpen && (
        <ConfirmMessage
          message={t("Are you sure you want to quit?")}
          onConfirm={() => {
            audioManager.stopAll();
            audioManager.play("s_auraBg");
            setNowOpen(null);
          }}
          onCancel={() => setIsConfirmQuitOpen(false)}
        />
      )}
      <div
        style={{ backgroundImage: `url(${dungeon?.img})` }}
        className="dungeon-container"
      >
        <div
          style={{
            position: "absolute",
            width: "100%",
            top: 24,
            left: 0,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              justifyContent: "space-around",
              backgroundColor: "rgb(0, 0, 0)",
              padding: "10px 12px",
              borderRadius: "4px 15px 15px 4px",
              fontSize: 12,
            }}
          >
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <HealthIcon size="12px" fill="red" />
              <div>{formatNumber(auraStats?.hp)}</div>
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <SpeedIcon size="12px" fill="green" />
              <div>{formatNumber(auraStats?.speed)}/sec</div>
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <DamageIcon size="12px" fill="orange" />
              <div>{formatNumber(auraStats?.damage)}</div>
            </div>
          </div>

          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              justifyContent: "space-around",
              backgroundColor: "rgb(0, 0, 0)",
              padding: "10px 12px",
              borderRadius: "15px 4px 4px 15px",
              fontSize: 12,
            }}
          >
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <HealthIcon size="12px" fill="red" />
              <div>{formatNumber(enemy?.hp)}</div>
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <SpeedIcon size="12px" fill="green" />
              <div>{formatNumber(enemy?.speed)}/sec</div>
            </div>
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <DamageIcon size="12px" fill="orange" />
              <div>{formatNumber(enemy?.damage)}</div>
            </div>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            position: "absolute",

            bottom: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "95%",
              gap: 25,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "45vw",
              }}
            >
              <DamageText
                eventName="enemy-damage"
                trigger={auraHpState}
                text={auraHpState}
              ></DamageText>
              <DungeonHpBar hp={auraStats.hp} hpNow={auraHpState} />
              {aura?.webm && (
                <>
                  {/* <motion.video
                  initial={{ x: 0 }} // Начальное положение
                  animate={{ x: 6 }} // Анимация до x: 4
                  transition={{
                    duration: 0.05, // Длительность анимации
                    repeat: 1, // Повторить анимацию 1 раз (вперед и назад)
                    repeatType: "reverse", // Тип повтора: обратная анимация
                  }}
                  key={hitCount.current}
                  width={"100%"}
                  src={aura.img}
                  autoPlay
                  muted
                  loop
                /> */}
                  <motion.img
                    initial={{ x: 0 }} // Начальное положение
                    animate={{ x: 6 }} // Анимация до x: 4
                    transition={{
                      duration: 0.05, // Длительность анимации
                      repeat: 1, // Повторить анимацию 1 раз (вперед и назад)
                      repeatType: "reverse", // Тип повтора: обратная анимация
                    }}
                    key={hitCount.current}
                    width={"100%"}
                    src={aura.miniimg}
                  ></motion.img>
                </>
              )}
              {!aura?.webm && (
                <motion.img
                  initial={{ x: 0 }} // Начальное положение
                  animate={{ x: 6 }} // Анимация до x: 4
                  transition={{
                    duration: 0.05, // Длительность анимации
                    repeat: 1, // Повторить анимацию 1 раз (вперед и назад)
                    repeatType: "reverse", // Тип повтора: обратная анимация
                  }}
                  key={hitCount.current}
                  width={"100%"}
                  src={aura.img}
                ></motion.img>
              )}

              {enemySlashAnimations.map((anim) => (
                <SlashAnimation
                  key={anim.id}
                  crit={anim.crit}
                  onComplete={() => {
                    setEnemySlashAnimations((prev) =>
                      prev.filter((animationId) => animationId !== anim.id)
                    );
                  }}
                />
              ))}
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "45vw",
              }}
            >
              <DamageText
                eventName="aura-damage"
                text={enemyHpState}
                trigger={enemyHpState}
              ></DamageText>
              <DungeonHpBar hp={enemy?.hp} hpNow={enemyHpState} />

              <motion.img
                className={enemyClass}
                initial={{ x: 0 }} // Начальное положение
                animate={{ x: -6 }} // Анимация до x: 4
                transition={{
                  duration: 0.05, // Длительность анимации
                  repeat: 1, // Повторить анимацию 1 раз (вперед и назад)
                  repeatType: "reverse", // Тип повтора: обратная анимация
                }}
                key={auraHpRef.current}
                width={"100%"}
                src={enemy?.img}
              ></motion.img>
              {auraSlashAnimations.map((anim) => (
                <SlashAnimation
                  key={anim.id}
                  crit={anim.crit}
                  onComplete={() => {
                    setEnemySlashAnimations((prev) =>
                      prev.filter((animationId) => animationId !== anim.id)
                    );
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="dungeon-stage-container ">
        <div>{t(enemy.title)}</div>

        <div className="dungeon-rooms-container">
          {roomsQuantity.map((_, index) => (
            <>
              {index === roomNumber - 1 &&
              index === roomsQuantity.length - 1 ? (
                <div key={index} className="dungeon-room-boss-active"></div>
              ) : index === roomNumber - 1 ? (
                <div key={index} className="dungeon-room-active"></div>
              ) : index === roomsQuantity.length - 1 ? (
                <div key={index} className="dungeon-room-boss"></div>
              ) : index !== roomNumber - 1 ? (
                <div key={index} className="dungeon-room"></div>
              ) : (
                ""
              )}
            </>
          ))}
        </div>
        <div className="dungeon-title">{t(dungeon.title)}</div>
      </div>

      <div className="buttons-ui-container">
        {!isFinished && (
          <div className="spin-buttons">
            <div style={{ display: "flex", gap: "8px", height: "40%" }}>
              <motion.button
                disabled={isFighting}
                style={{ backgroundColor: "rgb(153, 218, 49)" }}
                whileTap={{ scale: 0.97 }}
                onClick={startFight}
                transition={{ duration: 0.1 }}
                className="button-ui"
              >
                {t("Fight!")}
              </motion.button>
            </div>
          </div>
        )}
        <motion.button
          whileTap={{ scale: 0.97 }}
          transition={{ duration: 0.1 }}
          onClick={() => {
            handleQuit();
          }}
          className="button-ui"
        >
          {t("Quit")}
        </motion.button>
      </div>
    </div>
  );
};
export default Dungeon;
